import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, filter, finalize, take } from 'rxjs';
import { first, share } from 'rxjs/operators';

import { INTERVAL_TYPE } from '@main/enums';
import { GlobalConnectionService } from '@main/services/global-connection.service';
import { AuthHelpers } from '@shared/services';
import { RequestService } from '@shared/services/request/request.service';

import { ViewReportHelpers } from './view-report.helpers';
import { ViewReportCounters, ViewReportTokenData, ViewTokenParams } from './view-report.service.types';
import { LIMIT_AREA, LIMIT_TYPE } from '../components/limits-notification/limits-notification.types';
import { EntityHelpers } from '../helpers/entity.helpers';
import { EntityItem } from '../models';

@Injectable({ providedIn: 'root' })
export class ViewReportService {
    private requestService = inject(RequestService);
    private globalConnectionService = inject(GlobalConnectionService);

    viewToken$ = new BehaviorSubject<string>(null);
    viewCounters$ = new BehaviorSubject<ViewReportCounters | null>(null);

    get viewToken(): string {
        return this.viewToken$.value;
    }

    get viewCounters(): ViewReportCounters {
        return this.viewCounters$.getValue();
    }

    private viewTokenParams: ViewTokenParams;
    private isRefreshingToken = false;

    updateToken(params: ViewTokenParams): Observable<string> {
        this.viewTokenParams = { ...params };

        const { reportGroup, entityType, entities, filters, compare, benchmarkInfo } = this.viewTokenParams;

        const payloadEntities = this.buildPayloadEntities(entities, !!benchmarkInfo);

        const payload = ViewReportHelpers.composeRequestPayload(
            payloadEntities,
            filters,
            reportGroup,
            entityType,
            benchmarkInfo,
            compare,
        );

        if (params.dashboardId) {
            payload.dashboardId = params.dashboardId;
            payload.params.channel = undefined;
        }
        if (params.dashboardVersion !== undefined) {
            payload.dashboardVersion = params.dashboardVersion;
        }

        const token$ = this.requestService
            .post('security/tokens/report', payload, {
                headers: { Accept: 'text/plain' },
                responseType: 'text',
            })
            .pipe(share());

        token$.pipe(first()).subscribe({
            next: (token) => {
                this.viewToken$.next(token);

                if (token) {
                    const data = AuthHelpers.decodeToken<ViewReportTokenData>(token);

                    this.viewCounters$.next({
                        DAILY: data.usedReportViews?.DAILY,
                        MONTHLY: data.usedReportViews?.MONTHLY,
                    });
                }
            },
            error: () => {
                this.reset();
            },
        });

        return token$;
    }

    refreshToken(): Observable<string> {
        if (!this.isRefreshingToken) {
            this.isRefreshingToken = true;
            this.viewToken$.next(null);

            return this.updateToken(this.viewTokenParams).pipe(
                finalize(() => {
                    this.isRefreshingToken = false;
                }),
            );
        } else {
            return this.viewToken$.pipe(
                filter((token) => token != null),
                take(1),
            );
        }
    }

    reset(): void {
        this.viewToken$.next(null);
        this.viewTokenParams = null;
    }

    showViewLimitMessage(interval: INTERVAL_TYPE): void {
        this.globalConnectionService.showLimitMessage$.next([LIMIT_TYPE.VIEW, LIMIT_AREA.GLOBAL, interval]);
    }

    private buildPayloadEntities(entities: EntityItem[], isBenchmark: boolean): EntityItem[] {
        /* BE expect group GUID for:
           - saved comparison group (non-benchmark)
           - saved single entity group
           Or group's extracted entities otherwise
        */

        let payloadEntities = EntityHelpers.isItemsSingleGroup(entities)
            ? EntityHelpers.unwrapSingleGroup(entities)
            : entities;

        if (
            EntityHelpers.isItemsSingleComparisonGroup(entities) &&
            !EntityHelpers.isItemsVirtualGroup(entities) &&
            !isBenchmark
        ) {
            payloadEntities = entities;
        }

        return payloadEntities;
    }
}
